import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  FileText, CheckCircle2, XCircle, RefreshCw, ChevronRight, Loader2,
  Database, Sparkles, Search,
} from "lucide-react";

/**
 * AdminLessonsReviewPage — review the AI-drafted lessons in /api/admin/lessons.
 * Sortable table with status badges + per-row "Open" link to detail editor.
 */
const STATUS_COLORS = {
  pending: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  rejected: "bg-slate-500/10 text-slate-600 border-slate-500/30",
};

const AdminLessonsReviewPage = () => {
  const [drafts, setDrafts] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [searchQ, setSearchQ] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/admin/lessons/drafts${filter ? `?status=${filter}` : ""}`),
        api.get("/admin/lessons/coverage"),
      ]);
      setDrafts(d.drafts || []);
      setCoverage(c);
    } catch (e) {
      toast.error("Failed to load drafts");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const triggerIngest = async () => {
    setIngesting(true);
    try {
      const res = await api.post("/admin/lessons/ingest-pdfs", {});
      toast.success("PDF ingestion started — refreshing in 10s.");
      setTimeout(() => { load(); setIngesting(false); }, 10000);
    } catch (e) {
      toast.error("Failed to trigger ingest");
      setIngesting(false);
    }
  };

  const triggerBatch = async () => {
    if (!window.confirm("Generate drafts for ALL uncovered topics? This takes ~30 min and uses LLM credits.")) return;
    setGenerating(true);
    try {
      await api.post("/admin/lessons/generate-batch", {});
      toast.success("Batch generation queued — drafts will appear as they finish.");
      setTimeout(() => { load(); setGenerating(false); }, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start batch");
      setGenerating(false);
    }
  };

  const visible = drafts.filter((d) => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (d.name || "").toLowerCase().includes(q) || (d.subject_id || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-lessons-review-page" className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold mb-1">Lesson drafts</h1>
          <p className="text-sm text-muted-foreground">
            Review AI-drafted lessons grounded in your reference PDFs. Approve to publish into the Learn section.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={triggerIngest}
            disabled={ingesting}
            data-testid="admin-lessons-ingest"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-secondary hover:bg-secondary/80 border border-border disabled:opacity-50"
          >
            {ingesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
            Ingest PDFs
          </button>
          <button
            onClick={triggerBatch}
            disabled={generating}
            data-testid="admin-lessons-generate-batch"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Generate uncovered
          </button>
        </div>
      </div>

      {/* Coverage stats */}
      {coverage && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-lessons-coverage">
          <CoverageCard label="PDF chunks indexed" value={coverage.pdf_chunks_indexed} />
          <CoverageCard
            label="Planned"
            value={coverage.subjects.reduce((s, x) => s + x.planned, 0)}
          />
          <CoverageCard
            label="Drafted"
            value={coverage.subjects.reduce((s, x) => s + x.drafted, 0)}
          />
          <CoverageCard
            label="Pending review"
            value={coverage.subjects.reduce((s, x) => s + x.pending_review, 0)}
            highlight
          />
          <CoverageCard
            label="Live in Learn"
            value={coverage.subjects.reduce((s, x) => s + x.live_in_learn, 0)}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {["pending", "approved", "rejected", ""].map((f) => (
          <button
            key={f || "all"}
            onClick={() => setFilter(f)}
            data-testid={`admin-lessons-filter-${f || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
              filter === f
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-muted-foreground border-border hover:bg-secondary"
            }`}
          >
            {f === "" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search topic name or subject..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            data-testid="admin-lessons-search"
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg bg-card border border-border focus:outline-none focus:border-primary/40"
          />
        </div>
        <button
          onClick={load}
          data-testid="admin-lessons-refresh"
          className="p-2 rounded-lg hover:bg-secondary text-muted-foreground"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
          {filter === "pending" ? "No drafts pending review. Hit 'Generate uncovered' to create some." : "No drafts."}
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((d) => (
            <Link
              key={d.id}
              to={`/admin/lessons-review/${d.id}`}
              data-testid={`admin-lesson-row-${d.id}`}
              className="block border border-border rounded-xl p-4 bg-card hover:border-primary/40 hover:bg-secondary/20 transition-all"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="font-medium text-sm">{d.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_COLORS[d.status]}`}>
                      {d.status}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">{d.subject_id}</span>
                    {" · "}
                    {d.sections?.length || 0} sections
                    {" · "}
                    {d.key_takeaways?.length || 0} takeaways
                    {" · "}
                    {d.sources?.length || 0} sources
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const CoverageCard = ({ label, value, highlight }) => (
  <div className={`rounded-xl p-3 border ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-xl font-bold mt-1">{value}</div>
  </div>
);

export default AdminLessonsReviewPage;
